import { Link } from 'react-router-dom';
import { Youtube, Instagram, Linkedin, Shield } from 'lucide-react';
import TikTokIcon from '../icons/TikTokIcon';
import { useLanguage } from '@/context/LanguageContext';
import ModernLogo from '@/components/shared/ModernLogo';
import { LanguageSelectorFooter } from './LanguageSelectorFooter';
import T from '@/components/T';

const Footer = () => {
  const { getLocalizedPath } = useLanguage();
  
  return (
    <footer className="bg-brand-gray-50 border-t border-brand-gray-200">
      <div className="container mx-auto px-4 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Column 1: Logo and Description */}
          <div className="md:col-span-1">
            <Link to={getLocalizedPath('/')} className="inline-block">
              <ModernLogo />
            </Link>
            <p className="mt-4 text-brand-gray-600 text-sm">
              <T>globalPlatformDescription</T>
            </p>
            <div className="mt-4 flex space-x-4">
              <a 
                href="https://www.youtube.com/@FormacionPsicoterapia" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-brand-gray-500 hover:text-brand-blue-500 transition-colors"
              >
                <Youtube size={20} />
                <span className="sr-only">YouTube</span>
              </a>
              <a 
                href="https://www.instagram.com/formacionpsicoterapia/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-brand-gray-500 hover:text-brand-blue-500 transition-colors"
              >
                <Instagram size={20} />
                <span className="sr-only">Instagram</span>
              </a>
              <a 
                href="https://www.linkedin.com/company/formacion-psicoterapia/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-brand-gray-500 hover:text-brand-blue-500 transition-colors"
              >
                <Linkedin size={20} />
                <span className="sr-only">LinkedIn</span>
              </a>
              <a 
                href="https://www.tiktok.com/@formacionpsicoterapia"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-gray-500 hover:text-brand-blue-500 transition-colors"
              >
                <TikTokIcon size={20} />
                <span className="sr-only">TikTok</span>
              </a>
            </div>
          </div>
          
          {/* Column 2: For Clients */}
          <div>
            <h3 className="text-base font-semibold text-brand-gray-800 mb-4"><T>forClients</T></h3>
            <ul className="space-y-2">
              <li>
                <Link to={getLocalizedPath('/find')} className="text-brand-gray-600 hover:text-brand-blue-500 text-sm transition-colors">
                  <T>findTherapist</T>
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/specialties')} className="text-brand-gray-600 hover:text-brand-blue-500 text-sm transition-colors">
                  <T>browseBySpecialty</T>
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/cities')} className="text-brand-gray-600 hover:text-brand-blue-500 text-sm transition-colors">
                  <T>citiesDirectory</T>
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/countries')} className="text-brand-gray-600 hover:text-brand-blue-500 text-sm transition-colors">
                  <T>countriesDirectory</T>
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/online-therapy')} className="text-brand-gray-600 hover:text-brand-blue-500 text-sm transition-colors">
                  <T>onlineTherapy</T>
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/how-it-works')} className="text-brand-gray-600 hover:text-brand-blue-500 text-sm transition-colors">
                  <T>howItWorks</T>
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/pricing')} className="text-brand-gray-600 hover:text-brand-blue-500 text-sm transition-colors">
                  <T>pricing</T>
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/auth?role=client&mode=signup')} className="text-brand-gray-600 hover:text-brand-blue-500 text-sm transition-colors">
                  <span className="font-medium"><T>signUpAsClient</T></span>
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/auth?role=client')} className="text-brand-gray-600 hover:text-brand-blue-500 text-sm transition-colors">
                  <span className="font-medium"><T>clientLogin</T></span>
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Column 3: For Therapists */}
          <div>
            <h3 className="text-base font-semibold text-brand-gray-800 mb-4"><T>forTherapists</T></h3>
            <ul className="space-y-2">
              <li>
                <Link to={getLocalizedPath('/for-therapists')} className="text-brand-gray-600 hover:text-brand-blue-500 text-sm transition-colors">
                  <T>joinOurNetwork</T>
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/jose-luis-marin')} className="text-brand-gray-600 hover:text-brand-blue-500 text-sm transition-colors">
                  <T>trainingPrograms</T>
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/therapist-faq')} className="text-brand-gray-600 hover:text-brand-blue-500 text-sm transition-colors">
                  <T>therapistFAQ</T>
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/resources')} className="text-brand-gray-600 hover:text-brand-blue-500 text-sm transition-colors">
                  <T>resources</T>
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/auth?role=therapist&mode=signup')} className="text-brand-gray-600 hover:text-brand-blue-500 text-sm transition-colors">
                  <span className="font-medium"><T>joinAsTherapist</T></span>
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/auth?role=therapist')} className="text-brand-gray-600 hover:text-brand-blue-500 text-sm transition-colors">
                  <span className="font-medium"><T>therapistLogin</T></span>
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Column 4: About & Legal */}
          <div>
            <h3 className="text-base font-semibold text-brand-gray-800 mb-4"><T>aboutAndLegal</T></h3>
            <ul className="space-y-2">
              <li>
                <Link to={getLocalizedPath('/about')} className="text-brand-gray-600 hover:text-brand-blue-500 text-sm transition-colors">
                  <T>aboutUs</T>
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/jose-luis-marin')} className="text-brand-gray-600 hover:text-brand-blue-500 text-sm transition-colors">
                  <T>drJoseLuisMarin</T>
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/contact')} className="text-brand-gray-600 hover:text-brand-blue-500 text-sm transition-colors">
                  <T>contactUs</T>
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/privacy')} className="text-brand-gray-600 hover:text-brand-blue-500 text-sm transition-colors">
                  <T>privacyPolicy</T>
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/terms')} className="text-brand-gray-600 hover:text-brand-blue-500 text-sm transition-colors">
                  <T>termsOfService</T>
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/blog')} className="text-brand-gray-600 hover:text-brand-blue-500 text-sm transition-colors">
                  <T>blog</T>
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/auth?role=admin')} className="text-brand-gray-600 hover:text-brand-blue-500 text-sm transition-colors flex items-center">
                  <Shield size={16} className="mr-1" />
                  <span><T>adminPanel</T></span>
                </Link>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Language Selector Section */}
        <LanguageSelectorFooter />
        
        <div className="mt-8 pt-6 border-t border-border/40">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-brand-gray-500">
            <p>© {new Date().getFullYear()} psychotherapist.help. <T>allRightsReserved</T></p>
            <div className="mt-4 md:mt-0 flex flex-wrap space-x-4">
              <Link to={getLocalizedPath('/privacy')} className="hover:text-brand-blue-500 transition-colors">
                <T>privacy</T>
              </Link>
              <Link to={getLocalizedPath('/terms')} className="hover:text-brand-blue-500 transition-colors">
                <T>terms</T>
              </Link>
              <Link to={getLocalizedPath('/cookie-policy')} className="hover:text-brand-blue-500 transition-colors">
                <T>cookies</T>
              </Link>
              <Link to={getLocalizedPath('/sitemap')} className="hover:text-brand-blue-500 transition-colors">
                <T>sitemap</T>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
